import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import './PokerTableSVG.css';
import '../styles/TimerShowdown.css'; // ⏱ Timer animations


interface Card {
  rank: string;
  suit: 'hearts' | 'diamonds' | 'clubs' | 'spades';
}

interface Player {
  id: string;
  name: string;
  chips: number;
  currentBet?: number;
  folded?: boolean;
  allIn?: boolean;
  eliminated?: boolean;
  seat: number;
  holeCards?: Card[];
  //  Cash game status flags

  waitingForNextHand?: boolean;
  needsRebuy?: boolean;
  isReady?: boolean; //  Ready status for pre-game

  photoURL?: string; //  Avatar image URL

  // CoinPoker-style action pill: what this player just did this street
  lastAction?: string;
}

interface HandResult {
  winnerId: string;
  winnerName?: string;
  winningHand?: {
    type: string;
    cards?: Card[];
  };
  potWon: number;
  allOthersFolded?: boolean;
  winnerShowedCards?: boolean;
}

interface PokerTableSVGProps {
  players: Player[];
  currentPlayerIndex: number;
  pot: number;
  communityCards: Card[];
  currentUserId: string;
  round?: string;
  handResult?: HandResult | null;
  onShowCards?: () => void;
  //  Seat click handler - called with seat index when empty seat clicked

  onSeatClick?: (seatIndex: number) => void;
  maxSeats?: number;
  //  GAME ITEMS: Report player positions for animations

  onPlayerPositionsUpdate?: (positions: { [id: string]: { x: number; y: number } }) => void;
  //  RUNOUT SUSPENSE: When true, use communityCards as-is without filtering

  disableCardFiltering?: boolean;
  // ⏱ TURN TIMER: Props for 30-second countdown

  turnTimeLeft?: number;
  turnDuration?: number;
  //  EQUITY: Win percentages during all-in runout

  playerEquities?: { [playerId: string]: number };
  runoutPhase?: string;
  ritPhase?: string;
  //  FIX #1: Backend flag that persists (player.allIn gets cleared)

  allInShowdown?: boolean;
  allInOccurred?: boolean;
  // Tournament felt info lines (name / level / late reg countdown / rank) -
  // drawn dimly below the board inside table coordinates so cards and
  // seats can never collide with them.
  centerInfo?: string[];
  // Tournament chip leaders (top 3 uids, best first): gold/silver/bronze
  // medal on their avatar at every table, CoinPoker style.
  leaderBadges?: string[];
  // Tap-a-player profile: called with (playerId, playerName)
  onPlayerClick?: (playerId: string, playerName: string) => void;
  // Seat that just took a thrown item to the face - plays the hit shake
  hitPlayerId?: string | null;
  // Viewer's color tags (uid -> css color) - rings the tagged player's seat
  seatTags?: { [playerId: string]: string };
  // Run It Multiple Times: one board row per run, drawn on the felt where
  // the community cards normally sit. `visible` is how many cards of that
  // run have been dealt so far; `winnerText` appears under a finished run.
  ritBoards?: Array<{ cards: Card[]; visible: number; label: string; winnerText?: string; active: boolean }>;
}

const PokerTableSVG: React.FC<PokerTableSVGProps> = ({
  players: rawPlayers,
  currentPlayerIndex,
  pot,
  communityCards: rawCommunityCards,
  currentUserId,
  round = 'preflop',
  handResult = null,
  onShowCards,
  onSeatClick,
  maxSeats = 8,
  onPlayerPositionsUpdate,
  disableCardFiltering = false,
  turnTimeLeft = 30,
  turnDuration = 30,
  playerEquities = {},
  runoutPhase = 'none',
  ritPhase = 'none',
  allInShowdown = false,
  allInOccurred = false,
  centerInfo,
  leaderBadges,
  onPlayerClick,
  seatTags,
  hitPlayerId,
  ritBoards,
}) => {
  //  SAFE: Guard against undefined props AND filter null players

  const players = (rawPlayers || []).filter((p: any) => p && p.id);
  const communityCards = rawCommunityCards || [];
  
  // ── PORTRAIT MODE ─────────────────────────────────────────────────────
  // Phones get the same tall racetrack treatment as the premium felt;
  // desktop keeps the landscape oval untouched. matchMedia mirrors the CSS
  // breakpoints exactly so JS geometry and stylesheet can never disagree.
  const [isPortrait, setIsPortrait] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = () => setIsPortrait(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    window.addEventListener('resize', onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);

  const SVG_WIDTH = 700;
  const SVG_HEIGHT = isPortrait ? 1150 : 620;
  const CENTER_X = SVG_WIDTH / 2;
  const CENTER_Y = isPortrait ? 515 : 330; // landscape: height/2 + 20 for top-player room
  const TABLE_RX = 220;
  const TABLE_RY = 145;
  const SEATS_RADIUS_X = 280; // Horizontal radius for seats
  const SEATS_RADIUS_Y = 185; // Vertical radius for seats

  // Portrait seat anchors - clockwise from the hero at bottom center, each
  // sitting ON the racetrack rail centerline (stadium caps at (350,354) and
  // (350,676), r~292) so players straddle the table edge.
  const PORTRAIT_SEATS: { [k: number]: Array<[number, number]> } = {
    2: [[350, 970], [350, 62]],
    3: [[350, 970], [566, 158], [134, 158]],
    4: [[350, 970], [626, 510], [350, 62], [74, 510]],
    5: [[350, 970], [586, 848], [566, 158], [134, 158], [114, 848]],
    6: [[350, 970], [570, 868], [626, 400], [350, 62], [74, 400], [130, 868]],
    7: [[350, 970], [570, 868], [626, 470], [566, 158], [134, 158], [74, 470], [130, 868]],
    8: [[350, 970], [570, 868], [626, 510], [566, 158], [350, 62], [134, 158], [74, 510], [130, 868]],
  };

  //  Hover state for clickable seats

  const [hoveredSeat, setHoveredSeat] = useState<number | null>(null);

  //  SEAT ROTATION: Map visual positions around the table

  // Position 0 = bottom (where current user sits)
  // Positions go clockwise: bottom, bottom-right, right, top-right, top, top-left, left, bottom-left
  const VISUAL_SEAT_POSITIONS = [
    { angle: 90, label: 'bottom' },       // 0: Bottom center (current user)
    { angle: 45, label: 'bottom-right' }, // 1: Bottom right
    { angle: 0, label: 'right' },         // 2: Right
    { angle: 315, label: 'top-right' },   // 3: Top right
    { angle: 270, label: 'top' },         // 4: Top center
    { angle: 225, label: 'top-left' },    // 5: Top left
    { angle: 180, label: 'left' },        // 6: Left
    { angle: 135, label: 'bottom-left' }, // 7: Bottom left
  ];

  // Check if we're in showdown/ended state
  const isShowdown = round === 'showdown' || round === 'ended';
  const hasWinner = handResult && handResult.winnerId;
  
  // Show cards state for fold wins
  const [showCardsChosen, setShowCardsChosen] = useState(false);
  const [showCardsTimer, setShowCardsTimer] = useState(5);
  const [showCardsExpired, setShowCardsExpired] = useState(false);
  
  const isFoldWin = hasWinner && handResult?.allOthersFolded === true;
  const isCurrentUserWinner = hasWinner && handResult?.winnerId === currentUserId;
  const alreadyShowed = handResult?.winnerShowedCards === true;
  const canShowCards = isFoldWin && isCurrentUserWinner && !showCardsChosen && !showCardsExpired && !alreadyShowed;
  
  //  DEBUG: Log equity badge visibility

  useEffect(() => {
    const equityKeys = Object.keys(playerEquities);
    if (equityKeys.length > 0) {
      console.log(`📊 [SVG] Equities received! runoutPhase: "${runoutPhase}" | players with equity:`, equityKeys.length, playerEquities);
    } else if (runoutPhase !== 'none' && runoutPhase !== 'complete') {
      console.log(`📊 [SVG] Waiting for equities... runoutPhase: "${runoutPhase}" | equities: empty`);
    }
  }, [playerEquities, runoutPhase]);
  
  // Timer countdown for show cards option
  useEffect(() => {
    if (!canShowCards) return;
    
    const timer = setInterval(() => {
      setShowCardsTimer(prev => {
        if (prev <= 1) {
          setShowCardsExpired(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [canShowCards]);
  
  // Reset state when hand changes
  useEffect(() => {
    if (handResult) {
      setShowCardsChosen(false);
      setShowCardsExpired(false);
      setShowCardsTimer(5);
    }
  }, [handResult?.winnerId, handResult?.potWon]);
  
  const handleShowCards = useCallback(() => {
    setShowCardsChosen(true);
    if (onShowCards) {
      onShowCards();
    }
  }, [onShowCards]);

  //  Check if current user is in the game

  const currentUserInGame = useMemo(() => {
    return players.some(p => p && p.id === currentUserId && !p.eliminated);
  }, [players, currentUserId]);

  //  Get current user's seat index

  const currentUserSeat = useMemo(() => {
    const player = players.find(p => p && p.id === currentUserId);
    return player?.seat;
  }, [players, currentUserId]);

  //  Check if user can change seat (between hands or folded/waiting)

  const canChangeSeat = useMemo(() => {
    if (!currentUserInGame) return false;
    const currentPlayer = players.find(p => p && p.id === currentUserId);
    if (!currentPlayer) return false;
    
    //  FIX: Only allow seat change BETWEEN hands, never during active hand

    // Removing folded/waitingForNextHand as they can still corrupt runout animations
    return round === 'ended' || round === 'waiting';
  }, [currentUserInGame, players, currentUserId, round]);

  //  SEAT ROTATION LOGIC

  // Build a map of player ID -> visual position
  // Current user is always at visual position 0 (bottom)
  // Other players are distributed evenly around the table
  const playerToVisualPosition = useMemo(() => {
    const posMap = new Map<string, number>();
    if (!players || players.length === 0) return posMap;
    
    const numPlayers = players.length;
    const currentUserIndex = players.findIndex(p => p && p.id === currentUserId);
    
    if (currentUserIndex === -1) {
      // Current user not in game, just distribute players evenly
      players.forEach((p, idx) => {
        //  NULL GUARD: Skip null/invalid players

        if (!p || !p.id) return;
        //  FIXED: Use maxSeats instead of hardcoded 8

        const visualPos = Math.round((idx * maxSeats) / numPlayers) % maxSeats;
        posMap.set(p.id, visualPos);
      });
      return posMap;
    }
    
    // Distribute players evenly, with current user at position 0
    //  FIXED: Use maxSeats instead of hardcoded 8

    const spacing = maxSeats / numPlayers;
    players.forEach((player, idx) => {
      //  NULL GUARD: Skip null/invalid players

      if (!player || !player.id) return;
      // How many players away from current user (in seating order)
      const relativeIdx = (idx - currentUserIndex + numPlayers) % numPlayers;
      // Map to visual position
      //  FIXED: Use maxSeats instead of hardcoded 8

      const visualPos = Math.round(relativeIdx * spacing) % maxSeats;
      posMap.set(player.id, visualPos);
    });
    
    return posMap;
  }, [players, currentUserId, maxSeats]);

  // Reverse map: visual position -> player (if any)
  const visualPositionToPlayer = useMemo(() => {
    const reverseMap = new Map<number, typeof players[0] | undefined>();
    if (!players) return reverseMap;
    
    players.forEach(player => {
      //  NULL GUARD: Skip null/invalid players

      if (!player || !player.id) return;
      const visualPos = playerToVisualPosition.get(player.id);
      if (visualPos !== undefined) {
        reverseMap.set(visualPos, player);
      }
    });
    return reverseMap;
  }, [players, playerToVisualPosition]);

  //  GAME ITEMS: Report player positions for animation targeting

  //  FIXED: Use ref to prevent infinite loops - only update when positions actually change

  const lastPositionsRef = useRef<string>('');
  
  useEffect(() => {
    if (!onPlayerPositionsUpdate) return;
    
    const positions: { [id: string]: { x: number; y: number } } = {};
    
    players.forEach(player => {
      //  NULL GUARD: Skip null/invalid players

      if (!player || !player.id) return;
      const visualPos = playerToVisualPosition.get(player.id);
      if (visualPos !== undefined) {
        const pos = getSeatPosition(visualPos);
        positions[player.id] = { x: pos.x, y: pos.y };
      }
    });
    
    //  Only call update if positions actually changed

    const positionsKey = JSON.stringify(positions);
    if (positionsKey !== lastPositionsRef.current) {
      lastPositionsRef.current = positionsKey;
      onPlayerPositionsUpdate(positions);
    }
  }, [players, playerToVisualPosition, onPlayerPositionsUpdate]);


  //  FIXED: Dynamic angle calculation based on maxSeats

  const getSeatPosition = (visualPosition: number) => {
    if (isPortrait) {
      const anchors = PORTRAIT_SEATS[maxSeats] || PORTRAIT_SEATS[8];
      const a = anchors[visualPosition] || anchors[visualPosition % anchors.length];
      return { x: a[0], y: a[1], label: `seat-${visualPosition}` };
    }
    // Start at 90 degrees (bottom) and go clockwise
    const angleStep = 360 / maxSeats;
    const angle = 90 - (visualPosition * angleStep);
    const rad = (angle * Math.PI) / 180;

    return {
      x: CENTER_X + SEATS_RADIUS_X * Math.cos(rad),
      y: CENTER_Y + SEATS_RADIUS_Y * Math.sin(rad),
      label: `seat-${visualPosition}`,
    };
  };

  const getSuitSymbol = (suit: string) => {
    switch (suit) {
      case 'hearts': return '♥';

      case 'diamonds': return '♦';

      case 'clubs': return '♣';

      case 'spades': return '♠';

      default: return '';
    }
  };

  const getSuitColor = (suit: string) => {
    return suit === 'hearts' || suit === 'diamonds' ? '#dc2626' : '#1a1a1a';
  };

  //  Get chip color gradient based on bet amount - Industry Standard

  const getChipGradient = (amount: number): string => {
    if (amount >= 1000) return 'url(#chipGold)';     // Yellow/Gold: $1000+
    if (amount >= 500) return 'url(#chipPurple)';    // Purple: $500-999
    if (amount >= 100) return 'url(#chipBlack)';     // Black: $100-499
    if (amount >= 25) return 'url(#chipGreen)';      // Green: $25-99
    if (amount >= 5) return 'url(#chipRed)';         // Red: $5-24
    return 'url(#chipWhite)';                        // White: $1-4
  };

  //  Get chip stroke color to match gradient

  const getChipStroke = (amount: number): string => {
    if (amount >= 1000) return '#fbbf24';   // Gold stroke
    if (amount >= 500) return '#a855f7';    // Purple stroke
    if (amount >= 100) return '#6b7280';    // Gray stroke for black chip
    if (amount >= 25) return '#22c55e';     // Green stroke
    if (amount >= 5) return '#ef4444';      // Red stroke
    return '#d1d5db';                       // Light gray stroke for white
  };

  // Show correct number of community cards per round
  //  FIXED: SVG is DUMB - parent controls ALL visibility via communityCards prop

  const visibleCommunityCards = communityCards || [];

  // Render a single card
  const renderCard = (card: Card | undefined, x: number, y: number, width: number = 32, height: number = 45, key: string) => {
    if (!card || !card.rank || !card.suit) {
      // Card back
      return (
        <g key={key} transform={`translate(${x}, ${y})`}>
          <rect
            x="0"
            y="0"
            width={width}
            height={height}
            rx="4"
            fill="#c41e3a"
            stroke="#8b0000"
            strokeWidth="1.5"
          />
          <rect
            x="3"
            y="3"
            width={width - 6}
            height={height - 6}
            rx="2"
            fill="url(#cardBackPattern)"
            opacity="0.8"
          />
        </g>
      );
    }

    return (
      <g key={key} transform={`translate(${x}, ${y})`}>
        <rect
          x="0"
          y="0"
          width={width}
          height={height}
          rx="4"
          fill="#ffffff"
          stroke="#cccccc"
          strokeWidth="1"
          filter="url(#cardShadow)"
        />
        <text
          x="5"
          y="14"
          fill={getSuitColor(card.suit)}
          fontSize="12"
          fontWeight="bold"
          fontFamily="Arial, sans-serif"
        >
          {card.rank}
        </text>
        <text
          x={width / 2}
          y={height / 2 + 6}
          textAnchor="middle"
          fill={getSuitColor(card.suit)}
          fontSize="18"
          fontWeight="bold"
        >
          {getSuitSymbol(card.suit)}
        </text>
        {/* Bottom-right index, rotated 180 like a real deck */}
        <g transform={`rotate(180 ${width / 2} ${height / 2})`}>
          <text
            x="5"
            y="14"
            fill={getSuitColor(card.suit)}
            fontSize="12"
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
          >
            {card.rank}
          </text>
        </g>
      </g>
    );
  };

  //  Render seats using visual positions (rotated for current user)

  const seatElements = useMemo(() => {
    //  FIXED: Use maxSeats instead of hardcoded 8

    const visualSeats = Array.from({ length: maxSeats }, (_, visualPos) => {
      // Find player at this visual position using our map
      const player = visualPositionToPlayer.get(visualPos);
      
      const pos = getSeatPosition(visualPos);
      const isPlayerCurrentUser = player?.id === currentUserId;
      const isPlayerTurn = players[currentPlayerIndex]?.id === player?.id && !isShowdown;
      
      //  FIXED: Don't show winner highlight until runout animation completes

      // Check if this is an all-in showdown that needs runout
      const nonFoldedCount = players.filter(p => p && !p.folded).length;
      const isAllInShowdown = nonFoldedCount >= 2 && !isFoldWin;
      
      //  FIX: 'none' might mean animation was never needed (check-down)

      // 'complete' means animation finished OR was skipped
      // For safety, also allow 'none' after a short delay to prevent stuck states
      const runoutComplete = runoutPhase === 'complete' || ritPhase === 'complete';
      
      //  FIX #1: Use allInOccurred OR allInShowdown from backend

      // NEVER check player.allIn - it gets cleared by backend!
      // - allInOccurred: set immediately when any player goes all-in
      // - allInShowdown: set when runout begins
      const hasAllInFlag = allInOccurred || allInShowdown;
      const runoutWasNeeded = isAllInShowdown && hasAllInFlag;
      
      // Winner detection - only show AFTER runout completes for all-in showdowns
      // For fold wins or check-downs (!runoutWasNeeded), show winner immediately
      const rawIsWinner = hasWinner && player?.id === handResult?.winnerId;
      const isWinner = rawIsWinner && (!runoutWasNeeded || runoutComplete);
      
      // Debug: Log winner detection during showdown
      if (rawIsWinner && isShowdown) {
        console.log(`🏆 [SVG] Winner check: raw=${rawIsWinner}, runoutNeeded=${runoutWasNeeded}, runoutComplete=${runoutComplete}, final=${isWinner}, phase=${runoutPhase}, ritPhase=${ritPhase}, allInOccurred=${allInOccurred}, allInShowdown=${allInShowdown}`);
      }
      
      const potWonAmount = isWinner ? handResult?.potWon : 0;
      const winningHandType = isWinner ? handResult?.winningHand?.type : null;

      //  Players waiting for next hand should NEVER show cards

      const isWaitingPlayer = player?.waitingForNextHand || player?.needsRebuy;

      // Should we show this player's cards?
      const isNormalShowdown = isShowdown && !isFoldWin;
      const winnerChoseToShow = isWinner && isFoldWin && (showCardsChosen || handResult?.winnerShowedCards);
      
      //  FIX: Check if cards have ACTUAL values, not just array length

      // Hidden cards have [{rank: '', suit: ''}, {...}] which has length 2 but no values
      const hasValidCards = player?.holeCards && 
        player.holeCards.length === 2 && 
        player.holeCards[0]?.rank && 
        player.holeCards[0].rank !== '' &&
        player.holeCards[1]?.rank && 
        player.holeCards[1].rank !== '';
      
      // Logic for showing cards:
      // 1. NEVER show cards for waiting/rebuy players
      // 2. Current user can always see their own cards (unless they folded)
      // 3. In normal showdown (not fold win): show all non-folded players' cards IF they have valid cards
      // 4. In fold win: only show winner's cards if they chose to reveal
      // 5. All-in runout: everyone's cards are on their backs the moment the
      //    all-in showdown starts - the round is still flop/turn/river while
      //    the board runs out, so this can't rely on isNormalShowdown.
      const shouldShowCards = player && !player.folded && !isWaitingPlayer && (
        // You can always see your own cards
        (isPlayerCurrentUser && hasValidCards) ||
        // Normal showdown - everyone sees non-folded players' cards IF cards are revealed
        (isNormalShowdown && hasValidCards) ||
        // All-in runout - live hands are face up while the board deals
        (allInShowdown && hasValidCards) ||
        // Fold win - only if winner clicked "Show Cards"
        (winnerChoseToShow && hasValidCards)
      );

      //  Empty seat - CLICKABLE for join OR change seat

      if (!player) {
        const isHovered = hoveredSeat === visualPos;
        // Clickable if: onSeatClick provided AND (user not in game OR user can change seat)
        const isClickable = onSeatClick && !currentUserInGame;
        // Show different text based on whether user is already seated
        const actionText = currentUserInGame ? 'Change Seat' : 'Sit Here';
        
        return (
          <g
            key={`seat-${visualPos}`}
            transform={`translate(${pos.x}, ${pos.y})${isPortrait ? ' scale(1.5)' : ''}`}
            style={{ cursor: isClickable ? 'pointer' : 'default' }}
            onClick={() => isClickable && onSeatClick(visualPos)}
            onMouseEnter={() => isClickable && setHoveredSeat(visualPos)}
            onMouseLeave={() => setHoveredSeat(null)}
          >
            <circle
              cx="0"
              cy="0"
              r="28"
              fill={isHovered ? 'rgba(0, 200, 100, 0.3)' : 'rgba(0, 0, 0, 0.3)'}
              stroke={isHovered ? '#00c864' : 'rgba(255, 255, 255, 0.1)'}
              strokeWidth={isHovered ? 3 : 2}
              strokeDasharray={isHovered ? 'none' : '4 4'}
            />
            <text
              x="0"
              y={isHovered ? 0 : 4}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={isHovered ? '#00c864' : 'rgba(255, 255, 255, 0.3)'}
              fontSize={isHovered ? 10 : 20}
              fontWeight="bold"
            >
              {isHovered ? actionText : '+'}
            </text>
          </g>
        );
      }

      return (
        <g
          key={`seat-${visualPos}`}
          transform={`translate(${pos.x}, ${pos.y})${isPortrait ? ' scale(1.5)' : ''}`}
          className={`seat-group ${isPlayerTurn ? 'seat-active' : 'seat-inactive'} ${isWinner ? 'seat-winner' : ''} ${hitPlayerId === player.id ? 'seat-hit' : ''}`}
          onClick={onPlayerClick ? () => onPlayerClick(player.id, player.name || 'Player') : undefined}
          style={onPlayerClick ? { cursor: 'pointer' } : undefined}
        >
          {/* Viewer's color tag ring (CoinPoker-style player tagging) */}
          {seatTags && seatTags[player.id] && (
            <circle cx="0" cy="-14" r="28" fill="none"
              stroke={seatTags[player.id]} strokeWidth="3" opacity="0.95" />
          )}
          {/* 🥇 CHIP LEADER MEDAL - top-3 tournament stacks, all tables */}
          {(() => {
            const lRank = leaderBadges ? leaderBadges.indexOf(player.id) : -1;
            if (lRank < 0) return null;
            const medal = [
              { grad: 'url(#medalGold)', rim: '#ffe9a3', glow: 'rgba(241,201,79,0.65)', text: '#3a2a00' },
              { grad: 'url(#medalSilver)', rim: '#eef2f7', glow: 'rgba(205,214,224,0.55)', text: '#232b34' },
              { grad: 'url(#medalBronze)', rim: '#f0b57e', glow: 'rgba(208,138,74,0.55)', text: '#331c07' },
            ][lRank];
            // CoinPoker-style crest: star tip over a metallic medallion
            // pinned to the avatar's shoulder.
            return (
              <g transform="translate(-33, -44)">
                <circle r="15" fill={medal.glow} opacity="0.5" />
                <path d="M0,-19 L3.4,-11.5 L11,-11 L5.2,-6.4 L7,1 L0,-3 L-7,1 L-5.2,-6.4 L-11,-11 L-3.4,-11.5 Z"
                  transform="translate(0,-4) scale(0.82)"
                  fill={medal.grad} stroke={medal.rim} strokeWidth="1" />
                <circle r="11.5" fill={medal.grad} stroke={medal.rim} strokeWidth="1.6"
                  filter="url(#cardShadow)" />
                <circle r="8.6" fill="none" stroke="rgba(0,0,0,0.28)" strokeWidth="1" />
                <text x="0" y="4.2" textAnchor="middle" fontSize="12.5" fontWeight="900"
                  fill={medal.text}>{lRank + 1}</text>
              </g>
            );
          })()}

          {/* Winner Glow Effect */}
          {isWinner && (
            <ellipse
              cx="0"
              cy="0"
              rx="55"
              ry="60"
              fill="none"
              stroke="#FFD700"
              strokeWidth="3"
              opacity="0.6"
              className="winner-glow"
            />
          )}

          {/* Player's Hole Cards - Behind avatar */}
          {shouldShowCards && player.holeCards && player.holeCards.length >= 2 && (
            <g transform="translate(-22, -85)">
              {renderCard(player.holeCards[0], 0, 0, 30, 42, `${player.id}-card-0`)}
              {renderCard(player.holeCards[1], 20, 0, 30, 42, `${player.id}-card-1`)}
            </g>
          )}

          {/* Card Backs for other players - NOT for waiting/rebuy players,
              and NEVER on a parked felt (round 'waiting' = break/hold: the
              table is cleared, nobody holds cards) */}
          {!shouldShowCards && !player.folded && !isShowdown && !isWaitingPlayer
            && round !== 'waiting' && (
            <g transform="translate(-22, -85)">
              {renderCard(undefined, 0, 0, 30, 42, `${player.id}-back-0`)}
              {renderCard(undefined, 20, 0, 30, 42, `${player.id}-back-1`)}
            </g>
          )}

          {/* ⏱ TURN TIMER - Only subtle glow on player, main timer at top of screen */}

          {isPlayerTurn && !player.folded && !player.allIn && round !== 'ended' && round !== 'showdown' && (
            <circle
              cx="0"
              cy="-20"
              r="36"
              fill="none"
              stroke={turnTimeLeft <= 10 ? '#ff4444' : turnTimeLeft <= 20 ? '#ffaa00' : '#4CAF50'}
              strokeWidth="3"
              opacity="0.7"
              style={{
                filter: turnTimeLeft <= 10 
                  ? 'drop-shadow(0 0 10px #ff4444) drop-shadow(0 0 20px #ff4444)' 
                  : turnTimeLeft <= 20
                  ? 'drop-shadow(0 0 6px #ffaa00)'
                  : 'drop-shadow(0 0 6px #4CAF50)',
                animation: turnTimeLeft <= 10 ? 'pulse 0.5s ease-in-out infinite' : 'none'
              }}
            />
          )}

          {/* Simple turn indicator (for all-in players who have turn but no timer) */}
          {isPlayerTurn && (player.allIn || round === 'showdown' || round === 'ended') && (
            <circle
              cx="0"
              cy="-20"
              r="32"
              fill="none"
              stroke="#ff6b00"
              strokeWidth="3"
              className="turn-indicator"
            />
          )}

          {/* Avatar Circle - Background/fallback */}
          <circle
            cx="0"
            cy="-20"
            r="26"
            fill={isWinner ? 'url(#winnerAvatarGradient)' : isPlayerCurrentUser ? 'url(#currentUserGradient)' : 'url(#avatarGradient)'}
            stroke={isWinner ? '#FFD700' : isPlayerTurn ? '#ff6b00' : '#5a3d2b'}
            strokeWidth={isWinner ? 3 : isPlayerTurn ? 3 : 2}
          />
          
          {/*  Avatar Image - Show if photoURL exists */}

          {player.photoURL && (
            <g>
              {/* Clip circle for the image */}
              <defs>
                <clipPath id={`avatarClip-${player.id}`}>
                  <circle cx="0" cy="-20" r="24" />
                </clipPath>
              </defs>
              <image
                href={player.photoURL}
                x="-24"
                y="-44"
                width="48"
                height="48"
                clipPath={`url(#avatarClip-${player.id})`}
                preserveAspectRatio="xMidYMid slice"
              />
              {/* Border ring on top of image */}
              <circle
                cx="0"
                cy="-20"
                r="25"
                fill="none"
                stroke={isWinner ? '#FFD700' : isPlayerTurn ? '#ff6b00' : '#5a3d2b'}
                strokeWidth={isWinner ? 3 : isPlayerTurn ? 3 : 2}
              />
            </g>
          )}
          
          {/* Avatar Letter - Only show if no photoURL */}
          {!player.photoURL && (
            <text
              x="0"
              y="-16"
              textAnchor="middle"
              dominantBaseline="middle"
              fill={isWinner ? '#1a1a1a' : '#ffffff'}
              fontSize="18"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
            >
              {player.name?.charAt(0).toUpperCase()}
            </text>
          )}

          {/* Folded X overlay */}
          {player.folded && (
            <>
              <circle
                cx="0"
                cy="-20"
                r="26"
                fill="rgba(0, 0, 0, 0.6)"
              />
              <text
                x="0"
                y="-16"
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#888"
                fontSize="24"
                fontWeight="bold"
              >
                

              </text>
              <text
                x="0"
                y="0"
                textAnchor="middle"
                fill="#888"
                fontSize="8"
                fontWeight="bold"
              >
                FOLD
              </text>
            </>
          )}

          {/*  Status badge for READY (pre-game) */}

          {player.isReady && (
            <g transform="translate(0, -55)">
              <rect x="-25" y="0" width="50" height="14" rx="7" fill="#10b981" />
              <text x="0" y="10" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">READY</text>
            </g>
          )}

          {/*  Status badge for waiting for next hand (mid-game sit out) */}

          {player.waitingForNextHand && !player.isReady && (
            <g transform="translate(0, -55)">
              <rect x="-25" y="0" width="50" height="14" rx="7" fill="#f97316" />
              <text x="0" y="10" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">WAITING</text>
            </g>
          )}

          {/*  Status badge for needs rebuy */}

          {player.needsRebuy && (
            <g transform="translate(0, -55)">
              <rect x="-25" y="0" width="50" height="14" rx="7" fill="#dc2626" />
              <text x="0" y="10" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">REBUY?</text>
            </g>
          )}

          {/* Player Info Card */}
          <rect
            x="-45"
            y="10"
            width="90"
            height="36"
            rx="6"
            fill={isWinner ? 'rgba(255, 215, 0, 0.2)' : 'rgba(20, 25, 35, 0.95)'}
            stroke={isWinner ? '#FFD700' : isPlayerCurrentUser ? '#ff6b00' : 'rgba(100, 100, 120, 0.4)'}
            strokeWidth={isPlayerCurrentUser || isWinner ? 2 : 1}
          />

          {/* Trophy for winner */}
          {isWinner && (
            <text x="-35" y="24" fontSize="12"></text>

          )}

          {/* Player Name */}
          <text
            x={isWinner ? "-20" : "0"}
            y="24"
            textAnchor={isWinner ? "start" : "middle"}
            fill={isWinner ? '#FFD700' : '#ffffff'}
            fontSize="11"
            fontWeight="600"
            fontFamily="Arial, sans-serif"
          >
            {player.name?.slice(0, 10)}
          </text>

          {/* 🤖 BOT disclosure - pinned to the info card's top-right corner
              so it can never be clipped by a long name. This is the renderer
              the classic table actually uses (the DOM nameplate in
              PokerTable.tsx is a legacy path), so the tag must live here. */}
          {(String(player.id).startsWith('house_bot_') || String(player.id).startsWith('bot_')
            || (player as any).isBot === true) && (
            <g transform="translate(30, 10)">
              <rect x="-16" y="-6" width="32" height="12" rx="6"
                fill="#0d3b2e" stroke="#22c55e" strokeWidth="1" />
              <text y="3.5" textAnchor="middle" fill="#4ade80" fontSize="8"
                fontWeight="800" letterSpacing="1">BOT</text>
            </g>
          )}

          {/*  VISUAL CHIP STACK - Enterprise lightweight SVG */}

          {runoutPhase === 'none' || runoutPhase === 'complete' ? (
            <g transform="translate(-38, 33)">
              {/* Stack of 3 chips - color based on stack health */}
              {(() => {
                const chips = typeof player.chips === 'number' ? player.chips : 0;
                // Color based on stack size (relative to 100BB standard)
                const stackColor = chips >= 150 ? '#22c55e' : chips >= 75 ? '#eab308' : chips >= 25 ? '#f97316' : '#ef4444';
                const stackStroke = chips >= 150 ? '#15803d' : chips >= 75 ? '#ca8a04' : chips >= 25 ? '#ea580c' : '#dc2626';
                // Number of visible chips based on stack (1-3)
                const numChips = chips >= 100 ? 3 : chips >= 40 ? 2 : 1;
                
                return (
                  <>
                    {/* Bottom chip (always visible) */}
                    <ellipse cx="0" cy="0" rx="6" ry="3" fill={stackColor} stroke={stackStroke} strokeWidth="0.5" />
                    {/* Middle chip */}
                    {numChips >= 2 && (
                      <ellipse cx="0" cy="-3" rx="6" ry="3" fill={stackColor} stroke={stackStroke} strokeWidth="0.5" />
                    )}
                    {/* Top chip */}
                    {numChips >= 3 && (
                      <ellipse cx="0" cy="-6" rx="6" ry="3" fill={stackColor} stroke={stackStroke} strokeWidth="0.5" />
                    )}
                    {/* Highlight on top chip */}
                    <ellipse 
                      cx="0" 
                      cy={numChips === 3 ? -6 : numChips === 2 ? -3 : 0} 
                      rx="4" 
                      ry="2" 
                      fill="rgba(255,255,255,0.3)" 
                    />
                  </>
                );
              })()}
            </g>
          ) : null}

          {/* Chip Count Text */}
          <text
            x="5"
            y="38"
            textAnchor="middle"
            fill="#ff9950"
            fontSize="11"
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
          >
            {/* During all-in runout, hide chip counts to maintain suspense */}
            {runoutPhase !== 'none' && runoutPhase !== 'complete' 
              ? '...'
              : (typeof player.chips === 'number' ? player.chips : 0).toFixed(2)
            }
            {isWinner && potWonAmount > 0 && (
              <tspan fill="#10b981" fontWeight="bold"> +{potWonAmount.toFixed(2)}</tspan>
            )}
          </text>

          {/*  EQUITY BADGE - Shows win % during all-in runout */}

          {playerEquities[player.id] !== undefined && !player.folded
            && ((runoutPhase !== 'none' && runoutPhase !== 'complete') || (ritPhase !== 'none' && ritPhase !== 'complete')) && (
            <g transform="translate(40, -45)">
              {/* Larger, more visible badge */}
              <rect 
                x="-24" 
                y="-14" 
                width="48" 
                height="28" 
                rx="14" 
                fill={playerEquities[player.id] >= 50 ? '#22c55e' : '#ef4444'}
                stroke={playerEquities[player.id] >= 50 ? '#4ade80' : '#f87171'}
                strokeWidth="3"
                style={{ 
                  filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.4))',
                  animation: 'pulse 1s ease-in-out infinite'
                }}
              />
              <text 
                x="0" 
                y="4" 
                textAnchor="middle" 
                fill="#fff" 
                fontSize="14" 
                fontWeight="bold"
                fontFamily="Arial, sans-serif"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
              >
                {Math.round(playerEquities[player.id])}%
              </text>
            </g>
          )}

          {/*  BET CHIPS - Positioned toward table center (enterprise style) */}

          {typeof player.currentBet === 'number' && player.currentBet > 0 && !isShowdown && (
            (() => {
              // Calculate direction toward table center (portrait: the
              // seat group is scaled 1.5x, so offsets divide back out)
              const centerX = CENTER_X;
              const centerY = CENTER_Y;
              const dx = centerX - pos.x;
              const dy = centerY - pos.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              // Place chips 60% of the way toward center
              const chipOffsetX = (dx / distance) * (isPortrait ? 100 : 55);
              const chipOffsetY = (dy / distance) * (isPortrait ? 92 : 45);
              
              return (
                <g transform={`translate(${chipOffsetX}, ${chipOffsetY})`} className="bet-chip">
                  {/* Chip shadow */}
                  <ellipse
                    cx="2"
                    cy="4"
                    rx="18"
                    ry="10"
                    fill="rgba(0,0,0,0.4)"
                  />
                  {/* Stacked chips effect for larger bets */}
                  {player.currentBet >= 10 && (
                    <>
                      <ellipse cx="0" cy="6" rx="17" ry="9" fill={getChipGradient(player.currentBet)} stroke={getChipStroke(player.currentBet)} strokeWidth="1.5" />
                      <ellipse cx="0" cy="3" rx="17" ry="9" fill={getChipGradient(player.currentBet)} stroke={getChipStroke(player.currentBet)} strokeWidth="1.5" />
                    </>
                  )}
                  {player.currentBet >= 50 && (
                    <ellipse cx="0" cy="0" rx="17" ry="9" fill={getChipGradient(player.currentBet)} stroke={getChipStroke(player.currentBet)} strokeWidth="1.5" />
                  )}
                  {/* Top chip */}
                  <ellipse
                    cx="0"
                    cy={player.currentBet >= 50 ? -3 : player.currentBet >= 10 ? 0 : 0}
                    rx="17"
                    ry="9"
                    fill={getChipGradient(player.currentBet)}
                    stroke={getChipStroke(player.currentBet)}
                    strokeWidth="2"
                  />
                  {/* Chip highlight */}
                  <ellipse
                    cx="0"
                    cy={player.currentBet >= 50 ? -5 : player.currentBet >= 10 ? -2 : -2}
                    rx="12"
                    ry="5"
                    fill="rgba(255,255,255,0.25)"
                  />
                  {/* Chip center decoration */}
                  <ellipse
                    cx="0"
                    cy={player.currentBet >= 50 ? -3 : player.currentBet >= 10 ? 0 : 0}
                    rx="10"
                    ry="5"
                    fill="none"
                    stroke={getChipStroke(player.currentBet)}
                    strokeWidth="1"
                    opacity="0.6"
                  />
                  {/* Bet amount label */}
                  <rect
                    x="-18"
                    y={player.currentBet >= 50 ? 8 : player.currentBet >= 10 ? 12 : 10}
                    width="36"
                    height="14"
                    rx="7"
                    fill="rgba(0,0,0,0.85)"
                    stroke="rgba(255,255,255,0.3)"
                    strokeWidth="1"
                  />
                  <text
                    x="0"
                    y={player.currentBet >= 50 ? 18 : player.currentBet >= 10 ? 22 : 20}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize="10"
                    fontWeight="bold"
                    fontFamily="Arial, sans-serif"
                  >
                    ${player.currentBet >= 1000 ? (player.currentBet/1000).toFixed(1) + 'k' : player.currentBet.toFixed(0)}
                  </text>
                </g>
              );
            })()
          )}

          {/* Status Badges */}
          {player.allIn && !player.folded && (
            <g transform="translate(30, -35)">
              <rect x="-20" y="-8" width="40" height="16" rx="8" fill="#f97316" />
              <text x="0" y="3" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">ALL-IN</text>
            </g>
          )}

          {isPlayerTurn && !player.folded && !isShowdown && (
            <g transform="translate(0, 52)">
              <rect x="-25" y="-8" width="50" height="16" rx="8" fill="#ff6b00" />
              <text x="0" y="3" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">TURN</text>
            </g>
          )}

          {/* Last-action pill (CoinPoker style) - what this player just did
              this street: CHECK/CALL/RAISE/FOLD/ALL-IN. Hidden on the seat
              whose turn it is (TURN badge owns that spot) and between hands.
              The engine clears lastAction at each new betting round, so
              pills always describe the current street. */}
          {!isPlayerTurn && player.lastAction && !isShowdown && round !== 'ended' && (() => {
            const tag = String(player.lastAction).toUpperCase();
            const colors: Record<string, { bg: string; fg: string }> = {
              'RAISE':  { bg: '#f97316', fg: '#fff' },
              'CALL':   { bg: '#22c55e', fg: '#06281a' },
              'CHECK':  { bg: '#475569', fg: '#e2e8f0' },
              'FOLD':   { bg: '#6b7280', fg: '#f3f4f6' },
              'ALL-IN': { bg: '#dc2626', fg: '#fff' },
            };
            const c = colors[tag];
            if (!c) return null;
            if (tag === 'ALL-IN' && player.allIn) return null; // ALL-IN badge already shown above
            const w = Math.max(40, tag.length * 7 + 12);
            return (
              <g transform="translate(0, 52)">
                <rect x={-w / 2} y="-8" width={w} height="16" rx="8"
                  fill={c.bg} stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
                <text x="0" y="3" textAnchor="middle" fill={c.fg} fontSize="8" fontWeight="bold">{tag}</text>
              </g>
            );
          })()}

          {/* Dealer/SB/BB badges would go here */}
        </g>
      );
    });

    return visualSeats;
  }, [maxSeats, players, currentPlayerIndex, currentUserId, isShowdown, hasWinner, handResult, visualPositionToPlayer, showCardsChosen, isFoldWin, hoveredSeat, onSeatClick, currentUserInGame, canChangeSeat, runoutPhase, ritPhase, playerEquities, turnTimeLeft, turnDuration, round, allInShowdown, allInOccurred]);

  return (
    <div className="poker-table-svg-container">
      <svg
        width="100%"
        height="100%"
        // Portrait: headroom above y=0 so the top seat's card backs render
        // inside the viewBox instead of painting over the page chrome
        viewBox={isPortrait ? `0 -130 700 1290` : `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        className="poker-table-svg"
      >
        <defs>
          {/* Green Felt Gradient - ReBet Style */}
          <radialGradient id="feltGradient" cx="50%" cy="40%" r="70%">
            <stop offset="0%" stopColor="#1e7a1e" />
            <stop offset="50%" stopColor="#166616" />
            <stop offset="100%" stopColor="#0d4d0d" />
          </radialGradient>

          {/* Portrait: match the live premium felt exactly */}
          <radialGradient id="portraitFelt" cx="50%" cy="38%" r="78%">
            <stop offset="0%" stopColor="#2a8c5f" />
            <stop offset="55%" stopColor="#176849" />
            <stop offset="100%" stopColor="#0c3d2b" />
          </radialGradient>
          <linearGradient id="portraitRail" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6b3a26" />
            <stop offset="35%" stopColor="#4a261a" />
            <stop offset="70%" stopColor="#2e160e" />
            <stop offset="100%" stopColor="#1c0d08" />
          </linearGradient>

          {/* Table Rail Gradient */}
          <linearGradient id="railGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#d4a574" />
            <stop offset="30%" stopColor="#8B5A2B" />
            <stop offset="70%" stopColor="#654321" />
            <stop offset="100%" stopColor="#4a3010" />
          </linearGradient>

          {/* Orange Glow Gradient for outer edge */}
          <radialGradient id="outerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="85%" stopColor="transparent" />
            <stop offset="95%" stopColor="rgba(255, 140, 0, 0.3)" />
            <stop offset="100%" stopColor="rgba(255, 100, 0, 0.5)" />
          </radialGradient>

          {/* Avatar Gradients */}
          <linearGradient id="avatarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#5a3d2b" />
            <stop offset="100%" stopColor="#3d2a1e" />
          </linearGradient>

          <linearGradient id="currentUserGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ff9950" />
            <stop offset="100%" stopColor="#ff6432" />
          </linearGradient>

          <linearGradient id="winnerAvatarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFD700" />
            <stop offset="100%" stopColor="#FFA500" />
          </linearGradient>

          {/* Chip Gradients - Industry Standard Colors */}
          {/* White: $1-4 */}
          <linearGradient id="chipWhite" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#e5e5e5" />
          </linearGradient>
          {/* Red: $5-24 */}
          <linearGradient id="chipRed" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="100%" stopColor="#b91c1c" />
          </linearGradient>
          {/* Green: $25-99 */}
          <linearGradient id="chipGreen" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22c55e" />
            <stop offset="100%" stopColor="#15803d" />
          </linearGradient>
          {/* Black: $100-499 */}
          <linearGradient id="chipBlack" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#374151" />
            <stop offset="100%" stopColor="#111827" />
          </linearGradient>
          {/* Purple: $500-999 */}
          <linearGradient id="chipPurple" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
          {/* Yellow/Gold: $1000+ */}
          <linearGradient id="chipGold" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#d97706" />
          </linearGradient>

          {/* Card Back Pattern */}
          <radialGradient id="medalGold" cx="35%" cy="30%">
            <stop offset="0%" stopColor="#fff6c9" />
            <stop offset="45%" stopColor="#f1c94f" />
            <stop offset="80%" stopColor="#b8860b" />
            <stop offset="100%" stopColor="#8a6508" />
          </radialGradient>
          <radialGradient id="medalSilver" cx="35%" cy="30%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="45%" stopColor="#cdd6e0" />
            <stop offset="80%" stopColor="#8e9aa8" />
            <stop offset="100%" stopColor="#5f6b78" />
          </radialGradient>
          <radialGradient id="medalBronze" cx="35%" cy="30%">
            <stop offset="0%" stopColor="#ffd9b0" />
            <stop offset="45%" stopColor="#d08a4a" />
            <stop offset="80%" stopColor="#96562a" />
            <stop offset="100%" stopColor="#6d3c1b" />
          </radialGradient>
          <pattern id="cardBackPattern" width="8" height="8" patternUnits="userSpaceOnUse">
            <rect width="8" height="8" fill="#8b0000" />
            <path d="M0 0L8 8M8 0L0 8" stroke="#c41e3a" strokeWidth="1" />
          </pattern>

          {/* Card Shadow */}
          <filter id="cardShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.3" />
          </filter>

          {/* Glow Filters */}
          <filter id="tableGlow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="winnerGlow">
            <feGaussianBlur stdDeviation="6" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/*  Clip path for circular avatar images */}

          <clipPath id="avatarClip">
            <circle cx="26" cy="26" r="26" />
          </clipPath>
        </defs>

        {isPortrait ? (
          <>
            {/* Portrait racetrack - same treatment as the premium felt */}
            <rect x={22} y={26} width={656} height={958} rx={328}
              fill="url(#portraitRail)" stroke="#0d0d15" strokeWidth="4" />
            <rect x={22} y={26} width={656} height={958} rx={328}
              fill="none" stroke="#c9a44a" strokeOpacity="0.5" strokeWidth="2" />
            <rect x={58} y={62} width={584} height={906} rx={292}
              fill="url(#portraitFelt)" stroke="#0d0d15" strokeWidth="2" />
            <rect x={78} y={82} width={544} height={866} rx={272}
              fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2" />
            <text x={CENTER_X} y={CENTER_Y + 270} textAnchor="middle"
              fill="rgba(255,255,255,0.08)" fontSize="58" fontWeight="900"
              fontFamily="Arial Black" letterSpacing="6">PSS</text>
          </>
        ) : (
          <>
            {/* OUTER GLOW / RAIL */}
            <ellipse cx={CENTER_X} cy={CENTER_Y} rx={TABLE_RX + 15} ry={TABLE_RY + 15} fill="url(#outerGlow)" />

            {/* TABLE RAIL (Brown Wood) */}
            <ellipse cx={CENTER_X} cy={CENTER_Y} rx={TABLE_RX + 8} ry={TABLE_RY + 8}
              fill="url(#railGradient)" stroke="#4a3010" strokeWidth="3" />

            {/* GREEN FELT TABLE */}
            <ellipse cx={CENTER_X} cy={CENTER_Y} rx={TABLE_RX} ry={TABLE_RY}
              fill="url(#feltGradient)" stroke="#0d4d0d" strokeWidth="2" filter="url(#tableGlow)" />

            {/* Felt texture overlay */}
            <ellipse cx={CENTER_X} cy={CENTER_Y} rx={TABLE_RX - 5} ry={TABLE_RY - 5}
              fill="none" stroke="rgba(255, 255, 255, 0.05)" strokeWidth="1" />
          </>
        )}

        {/* POT DISPLAY */}
        <g transform={`translate(${CENTER_X}, ${isPortrait ? 250 : CENTER_Y - 50})${isPortrait ? ' scale(1.4)' : ''}`}>
          <circle
            cx="0"
            cy="0"
            r="28"
            fill="rgba(0, 0, 0, 0.3)"
            stroke="rgba(255, 255, 255, 0.2)"
            strokeWidth="2"
          />
          <text
            x="0"
            y="-8"
            textAnchor="middle"
            fill="rgba(255, 255, 255, 0.7)"
            fontSize="10"
            fontWeight="600"
          >
            Main Pot
          </text>
          <text
            x="0"
            y="10"
            textAnchor="middle"
            fill={isShowdown && hasWinner ? '#10b981' : '#ffffff'}
            fontSize="16"
            fontWeight="bold"
          >
            ${typeof pot === 'number' ? pot.toFixed(2) : '0.00'}
          </text>
        </g>

        {/* COMMUNITY CARDS - portrait doubles them so the board reads at
            arm's length, matching the premium felt. Run It Multiple Times
            draws one row per run in the same place: run 1 stays on the felt
            with its winner once it has finished, run 2 deals beneath it. */}
        {(() => {
          const rows = ritBoards && ritBoards.length > 0
            ? ritBoards
            : [{ cards: visibleCommunityCards, visible: visibleCommunityCards.length, label: '', winnerText: '', active: true }];
          const multi = rows.length > 1;
          const rowGap = isPortrait ? 158 : 64;
          const baseY = isPortrait ? (multi ? 236 : 330) : (multi ? CENTER_Y - 24 : CENTER_Y + 10);
          return rows.map((row, r) => (
            <g key={`board-row-${r}`}
              transform={isPortrait
                ? `translate(${CENTER_X - 225}, ${baseY + r * rowGap}) scale(2.5)`
                : `translate(${CENTER_X - 90}, ${baseY + r * rowGap})`}
              opacity={row.active ? 1 : 0.8}>
              {row.label && (
                <text x="-10" y="30" textAnchor="end" fill={row.active ? '#fde68a' : 'rgba(255,255,255,0.55)'} fontSize="11" fontWeight="800" letterSpacing="1">
                  {row.label}
                </text>
              )}
              {[0, 1, 2, 3, 4].map((idx) => {
                const card = row.cards?.[idx];
                const isCardVisible = idx < (row.visible || 0);
                const cardX = idx * 38;
                return (
                  <g
                    key={`comm-card-${r}-${idx}`}
                    transform={`translate(${cardX}, 0)`}
                    className={isCardVisible ? 'card-visible' : ''}
                  >
                    {isCardVisible && card && card.rank && card.suit ? (
                      <>
                        <rect x="0" y="0" width="34" height="48" rx="4" fill="#ffffff" stroke="#cccccc" strokeWidth="1" filter="url(#cardShadow)" />
                        <text x="6" y="14" fill={getSuitColor(card.suit)} fontSize="12" fontWeight="bold">{card.rank}</text>
                        <text x="17" y="34" textAnchor="middle" fill={getSuitColor(card.suit)} fontSize="20" fontWeight="bold">{getSuitSymbol(card.suit)}</text>
                      </>
                    ) : (
                      <rect x="0" y="0" width="34" height="48" rx="4" fill="rgba(255, 255, 255, 0.05)" stroke="rgba(255, 255, 255, 0.15)" strokeWidth="1.5" strokeDasharray="4 2" />
                    )}
                  </g>
                );
              })}
              {row.winnerText && (
                <g transform="translate(93, 58)">
                  <rect x="-80" y="-9" width="160" height="16" rx="8" fill="rgba(0,0,0,0.55)" stroke="#22c55e" strokeWidth="1" />
                  <text x="0" y="3" textAnchor="middle" fill="#4ade80" fontSize="9.5" fontWeight="800">{row.winnerText}</text>
                </g>
              )}
            </g>
          ));
        })()}

        {/* TOURNAMENT FELT INFO - dim lines in the safe gap between the
            board (bottom edge CENTER_Y+58) and the bottom seats (~+155).
            Inside table coordinates, so cards can never collide. */}
        {centerInfo && centerInfo.length > 0 && (
          <g pointerEvents="none">
            {centerInfo.slice(0, 4).map((line, i, arr) => (
              <text
                key={`felt-info-${i}`}
                x={CENTER_X}
                // Anchored UPWARD from a fixed bottom line: when extra rows
                // appear (HAND FOR HAND, pay jumps) the block grows toward
                // the board, never down under the hero's hole cards.
                y={CENTER_Y + 100 - (arr.length - 1 - i) * 12}
                textAnchor="middle"
                fill="rgba(226,232,240,0.5)"
                fontSize="10"
                fontWeight="600"
              >
                {line}
              </text>
            ))}
          </g>
        )}

        {/* PLAYER SEATS */}
        {seatElements}

        {/* SHOW CARDS BUTTON - Only for fold win winner */}
        {canShowCards && (
          <foreignObject x={CENTER_X - 80} y={CENTER_Y + 85} width="160" height="50">
            <div className="show-cards-container">
              <button 
                className="show-cards-button"
                onClick={handleShowCards}
              >
                 SHOW CARDS ({showCardsTimer}s)

              </button>
            </div>
          </foreignObject>
        )}
      </svg>
    </div>
  );
};

export default PokerTableSVG;